// RentedProperties Demo Steps Configuration
// This file contains all demo steps for pages under /Pages/RentedProperties/

export const rentedPropertiesIndexSteps = [
  {
    title: "Welcome to Rental Payments!",
    content: `
      <p class="mb-3">This is your Rental Payments dashboard where you manage rent collection for your properties.</p>
      <p>Track payments, manage tenants, and handle rent collection all in one place.</p>
    `,
    info: "Online rent collection makes payment management much easier for both landlords and tenants.",
    target: null
  },
  {
    title: "Add Property for Rent Collection",
    content: `
      <p class="mb-3">Click <strong>Add Property</strong> to set up rent collection for a new property.</p>
      <p>This enables tenants to pay rent online and provides you with payment tracking.</p>
    `,
    info: "Properties must be added here before tenants can make online rent payments.",
    target: "add-property-btn",
    modalOffset: { x: -40, y: 0 }
  },
  {
    title: "Rental Properties Overview",
    content: `
      <p class="mb-3">View all properties with rent collection enabled:</p>
      <ul class="list-disc ml-5 space-y-1">
        <li><strong>Address:</strong> Property location</li>
        <li><strong>Tenant:</strong> Current renter information</li>
        <li><strong>Lease Term:</strong> Rental period details</li>
        <li><strong>Monthly Rent:</strong> Rent amount</li>
      </ul>
    `,
    info: "Click on any property to see detailed payment history and tenant information.",
    target: "rental-properties-table"
  },
  {
    title: "Search and Filter",
    content: `
      <p class="mb-3">Use the <strong>search tools</strong> to find specific properties:</p>
      <ul class="list-disc ml-5 space-y-1">
        <li>Search by property address</li>
        <li>Filter by tenant name</li>
        <li>Show/hide archived properties</li>
        <li>Adjust results per page</li>
      </ul>
    `,
    info: "These tools help you quickly locate specific properties in large portfolios.",
    target: "search-filters"
  },
  {
    title: "Transaction Management",
    content: `
      <p class="mb-3">Access your <strong>payment transaction tools</strong>:</p>
      <ul class="list-disc ml-5 space-y-1">
        <li><strong>My Transactions:</strong> View all payment history</li>
        <li><strong>My Payouts:</strong> Track money transfers to your account</li>
      </ul>
    `,
    info: "These views provide complete financial tracking for your rental income.",
    target: "transaction-buttons",
    modalOffset: { x: -40, y: 0 }
  }
]

export const rentedPropertiesCreateSteps = [
  {
    title: "Welcome to Add Rental Property!",
    content: `
      <p class="mb-3">Set up online rent collection for a new property.</p>
      <p>This enables tenants to pay rent online and provides automated payment tracking.</p>
    `,
    info: "Online rent collection reduces late payments and simplifies accounting.",
    target: null
  },
  {
    title: "Use Existing Address",
    content: `
      <p class="mb-3">Select an <strong>existing property address</strong> from your portfolio 
      or choose to enter a new one manually.</p>
      <p>This saves time if you already manage multiple properties in the system.</p>
    `,
    info: "Selecting an existing address auto-fills the property details for you.",
    target: "use-existing-address"
  },
  {
    title: "Property Address",
    content: `
      <p class="mb-3">Enter or verify the <strong>property address</strong> for rent collection.</p>
      <p>This address will appear on tenant payment receipts and communications.</p>
    `,
    info: "Accurate address information is important for payment processing and records.",
    target: "address_lookup",
    modalOffset: { x: -40, y: -40 }
  },
  {
    title: "Monthly Rent",
    content: `
      <p class="mb-3">Enter the <strong>monthly rent amount</strong> for this property.</p>
      <p>This amount will be used for display and tenant setup when creating the property.</p>
    `,
    info: "Make sure the rent amount matches the lease agreement with your tenant.",
    target: "rent-amount",
    modalOffset: { x: -40, y: -40 }
  },
  {
    title: "Save Property",
    content: `
      <p class="mb-3">Click <strong>Save</strong> to activate online rent collection for this property.</p>
      <p>Your tenant will receive instructions for making online payments.</p>
    `,
    info: "Once activated, tenants can immediately start making online rent payments.",
    target: "save-button",
    modalOffset: { x: -40, y: -40 }
  }
]

export const rentedPropertiesEditSteps = [
  {
    title: "Welcome",
    content: `
      <p class="mb-3">Welcome to the <strong>Edit Property</strong> settings.</p>
      <p>Here you can update key details for your rental property.</p>
    `,
    info: "Use these settings to keep your rent payments flowing smoothly.",
    target: null
  },
  {
    title: "Property Address",
    content: `
      <p class="mb-3">Update the <strong>property address</strong> here.</p>
      <p>Keeping the address accurate ensures correct records for you and tenants.</p>
    `,
    info: "Correct addresses help avoid confusion in communications.",
    target: "address_lookup",
    modalOffset: { x: -40, y: 0 }
  },
  {
    title: "Monthly Rent",
    content: `
      <p class="mb-3">Update the <strong>monthly rent amount</strong> shown for this property.</p>
      <p><em>Note:</em> Changes made here do <strong>not</strong> affect current or future tenant rent payments. 
      To update rent for billing and invoices, please make the change in the 
      <strong>Lease Information</strong> section of the property.</p>
    `,
    info: "Use this field for display purposes only. For actual rent changes, edit the property's lease information.",
    target: "rent-amount",
    modalOffset: { x: -40, y: -40 }
  },
  {
    title: "Bank Account",
    content: `
      <p class="mb-3">Choose the <strong>bank account</strong> where rent payments will be deposited.</p>
      <p>Make sure the right account is selected to receive payouts.</p>
    `,
    info: "Updating your bank ensures uninterrupted rent deposits.",
    target: "bank-account",
    modalOffset: { x: -40, y: 0 }
  },
  {
    title: "Save Updates",
    content: `
      <p class="mb-3">Click <strong>Update</strong> to apply your changes.</p>
      <p>All updates will reflect in rent collection and payouts.</p>
    `,
    info: "Changes only take effect once updated.",
    target: "save-updates",
    modalOffset: { x: -40, y: 0 }
  }
]

export const rentedPropertiesLeaseInformationSteps = [
  {
    title: "Lease Information Management",
    content: `
      <p class="mb-3">Enter and manage detailed <strong>lease information</strong> for your rental property.</p>
      <p>This ensures accurate rent billing and smooth tenant communication.</p>
    `,
    info: "Fill in each field step by step to complete the lease setup.",
    target: null
  },
  {
    title: "Tenant Email",
    content: `
      <p class="mb-3">Enter the <strong>tenant’s email address</strong>.</p>
      <p>An invitation will be sent if the tenant has not yet joined RentRisk, along with their lease details.</p>
    `,
    info: "Tenant email is required for communication and account setup.",
    target: "tenant-email"
  },
  {
    title: "Lease Start Date",
    content: `
      <p class="mb-3">Select the <strong>date the lease begins</strong>.</p>
      <p>This marks the official start of the lease term.</p>
    `,
    info: "The start date defines when the tenant’s agreement begins.",
    target: "lease-start-date",
    modalOffset: { x: -40, y: 0 }
  },
  {
    title: "Lease End Date",
    content: `
      <p class="mb-3">Select the <strong>date the lease ends</strong>.</p>
      <p>This marks the official expiration of the lease term.</p>
    `,
    info: "The end date defines when the lease agreement finishes.",
    target: "lease-end-date",
    modalOffset: { x: -40, y: 0 }
  },
  {
    title: "Move-In Date",
    content: `
      <p class="mb-3">Enter the <strong>move-in date</strong> for the tenant.</p>
      <p>This records when the tenant physically occupies the property.</p>
    `,
    info: "Move-in date may differ from the lease start date.",
    target: "move-in-date"
  },
  {
    title: "Rent Starts From",
    content: `
      <p class="mb-3">Select the <strong>month and date</strong> when rent collection begins.</p>
      <p>Please add the day of the month the rent is due.</p>
    `,
    target: "rent-starts-from",
    modalOffset: { x: -40, y: 0 }
  },
  {
    title: "Monthly Rent",
    content: `
      <p class="mb-3">Enter the <strong>monthly rent amount</strong> for this lease.</p>
    `,
    info: "This value is used to generate rent invoices.",
    target: "monthly-rent",
    modalOffset: { x: -40, y: 0 }
  },
  {
    title: "Rent Due Date",
    content: `
      <p class="mb-3">Choose the <strong>day of the month</strong> rent is due.</p>
    `,
    info: "Common options are the 1st or 15th of the month.",
    target: "rent-due-date"
  },
  {
    title: "Rent Considered Late",
    content: `
      <p class="mb-3">Choose the <strong>day of the month</strong> after which rent is considered late if unpaid.</p>
    `,
    info: "Must be on or after the rent due day. Example: due on the 1st, considered late after the 5th.",
    target: "rent-late-on-date"
  },
  {
    title: "Late Charges Type",
    content: `
      <p class="mb-3">Select the <strong>late fee type</strong>:</p>
      <ul class="list-disc ml-5 space-y-1">
        <li>Daily</li>
        <li>Fixed</li>
      </ul>
    `,
    info: "This determines how late fees are applied to overdue rent.",
    target: "late-charges-type",
    modalOffset: { x: -40, y: 0 }
  },
  {
    title: "Late Charges Mode",
    content: `
      <p class="mb-3">Choose how late charges are applied:</p>
      <ul class="list-disc ml-5 space-y-1">
        <li><strong>Amount</strong> (flat dollar fee)</li>
        <li><strong>Percentage</strong> (of rent amount)</li>
      </ul>
    `,
    info: "Select whether the late fee is a fixed amount or percentage-based.",
    target: "late-charges-mode",
    modalOffset: { x: -40, y: 0 }
  },
  {
    title: "Late Charges Fee",
    content: `
      <p class="mb-3">Enter the <strong>late fee value</strong>.</p>
      <p>This can be either a dollar amount or percentage, depending on your selection.</p>
    `,
    info: "Tenants will see this fee applied if they miss payments.",
    target: "late-charges-fee"
  },
  {
    title: "Payment Method",
    content: `
      <p class="mb-3">Select the <strong>ACH payment method</strong> for this lease:</p>
      <ul class="list-disc ml-5 space-y-1">
        <li><span class="font-bold">Same Day ACH</span> – 2-3 business days | $4 fee</li>
        <li><span class="font-bold">Standard ACH</span> – 4-5 business days | $3 fee</li>
      </ul>
    `,
    info: "This controls how tenant payments are processed.",
    target: "payment-method",
    modalOffset: { x: -40, y: 0 }
  },
  {
    title: "Notes",
    content: `
      <p class="mb-3">Add any <strong>optional notes</strong> about this lease.</p>
      <p>These are for your records only and are not sent to the tenant.</p>
    `,
    info: "Notes can include reminders, conditions, or other internal comments.",
    target: "lease-notes"
  },
  {
    title: "One-Time Charges",
    content: `
      <p class="mb-3">Enter any <strong>one-time charges</strong> associated with this lease.</p>
      <p>You may add multiple charges such as deposits, pet fees, or admin fees.</p>
    `,
    info: "These charges are billed separately from monthly rent.",
    target: "one-time-charges"
  },
  {
    title: "Save Lease",
    content: `
      <p class="mb-3">Click <strong>Save</strong> to finalize this lease.</p>
      <p>Once saved, the tenant will be notified and rent billing will begin.</p>
    `,
    info: "Be sure all fields are completed before saving.",
    target: "save-button",
    modalOffset: { x: -40, y: 0 }
  }
]

export const rentedPropertiesLeaseInformationEditSteps = [
  {
    title: "Lease Information Management",
    content: `
      <p class="mb-3">Enter and manage detailed <strong>lease information</strong> for your rental property.</p>
      <p>This ensures accurate rent billing and smooth tenant communication.</p>
    `,
    info: "Fill in each field step by step to complete the lease setup.",
    target: null
  },
  {
    title: "Complete Lease",
    content: `
      <p class="mb-3">Click <strong>Complete Lease</strong> when the current lease term has ended.</p>
      <p>This marks the lease as completed and stops further rent collection or tenant billing under this lease.</p>
    `,
    info: "Use this action only once the tenant has fully moved out or the lease term has officially ended.",
    target: "complete-lease-button"
  },
  {
    title: "Tenant Email",
    content: `
      <p class="mb-3">Enter the <strong>tenant’s email address</strong>.</p>
      <p>An invitation will be sent if the tenant has not yet joined RentRisk, along with their lease details.</p>
    `,
    info: "Tenant email is required for communication and account setup.",
    target: "tenant-email"
  },
  {
    title: "Lease Start Date",
    content: `
      <p class="mb-3">Select the <strong>date the lease begins</strong>.</p>
      <p>This marks the official start of the lease term.</p>
    `,
    info: "The start date defines when the tenant’s agreement begins.",
    target: "lease-start-date",
    modalOffset: { x: -40, y: 0 }
  },
  {
    title: "Lease End Date",
    content: `
      <p class="mb-3">Select the <strong>date the lease ends</strong>.</p>
      <p>This marks the official expiration of the lease term.</p>
    `,
    info: "The end date defines when the lease agreement finishes.",
    target: "lease-end-date",
    modalOffset: { x: -40, y: 0 }
  },
  {
    title: "Move-In Date",
    content: `
      <p class="mb-3">Enter the <strong>move-in date</strong> for the tenant.</p>
      <p>This records when the tenant physically occupies the property.</p>
    `,
    info: "Move-in date may differ from the lease start date.",
    target: "move-in-date"
  },
  {
    title: "Rent Starts From",
    content: `
      <p class="mb-3">Select the <strong>month and date</strong> when rent collection begins.</p>
      <p>Please add the day of the month the rent is due.</p>
    `,
    target: "rent-starts-from",
    modalOffset: { x: -40, y: 0 }
  },
  {
    title: "Monthly Rent",
    content: `
      <p class="mb-3">Enter the <strong>monthly rent amount</strong> for this lease.</p>
    `,
    info: "This value is used to generate rent invoices.",
    target: "monthly-rent",
    modalOffset: { x: -40, y: 0 }
  },
  {
    title: "Rent Due Date",
    content: `
      <p class="mb-3">Choose the <strong>day of the month</strong> rent is due.</p>
    `,
    info: "Common options are the 1st or 15th of the month.",
    target: "rent-due-date"
  },
  {
    title: "Rent Considered Late",
    content: `
      <p class="mb-3">Choose the <strong>day of the month</strong> after which rent is considered late if unpaid.</p>
    `,
    info: "Must be on or after the rent due day. Example: due on the 1st, considered late after the 5th.",
    target: "rent-late-on-date"
  },
  {
    title: "Late Charges Type",
    content: `
      <p class="mb-3">Select the <strong>late fee type</strong>:</p>
      <ul class="list-disc ml-5 space-y-1">
        <li>Daily</li>
        <li>Fixed</li>
      </ul>
    `,
    info: "This determines how late fees are applied to overdue rent.",
    target: "late-charges-type",
    modalOffset: { x: -40, y: 0 }
  },
  {
    title: "Late Charges Mode",
    content: `
      <p class="mb-3">Choose how late charges are applied:</p>
      <ul class="list-disc ml-5 space-y-1">
        <li><strong>Amount</strong> (flat dollar fee)</li>
        <li><strong>Percentage</strong> (of rent amount)</li>
      </ul>
    `,
    info: "Select whether the late fee is a fixed amount or percentage-based.",
    target: "late-charges-mode",
    modalOffset: { x: -40, y: 0 }
  },
  {
    title: "Late Charges Fee",
    content: `
      <p class="mb-3">Enter the <strong>late fee value</strong>.</p>
      <p>This can be either a dollar amount or percentage, depending on your selection.</p>
    `,
    info: "Tenants will see this fee applied if they miss payments.",
    target: "late-charges-fee"
  },
  {
    title: "Payment Method",
    content: `
      <p class="mb-3">Select the <strong>ACH payment method</strong> for this lease:</p>
      <ul class="list-disc ml-5 space-y-1">
        <li><span class="font-bold">Same Day ACH</span> – 2-3 business days | $4 fee</li>
        <li><span class="font-bold">Standard ACH</span> – 4-5 business days | $3 fee</li>
      </ul>
    `,
    info: "This controls how tenant payments are processed.",
    target: "payment-method",
    modalOffset: { x: -40, y: 0 }
  },
  {
    title: "Notes",
    content: `
      <p class="mb-3">Add any <strong>optional notes</strong> about this lease.</p>
      <p>These are for your records only and are not sent to the tenant.</p>
    `,
    info: "Notes can include reminders, conditions, or other internal comments.",
    target: "lease-notes"
  },
  {
    title: "One-Time Charges",
    content: `
      <p class="mb-3">Enter any <strong>one-time charges</strong> associated with this lease.</p>
      <p>You may add multiple charges such as deposits, pet fees, or admin fees.</p>
    `,
    info: "These charges are billed separately from monthly rent.",
    target: "one-time-charges"
  },
  {
    title: "Update Lease",
    content: `
      <p class="mb-3">Click <strong>Update</strong> to finalize this lease.</p>
      <p>Once saved, the tenant will be notified and rent billing will begin.</p>
    `,
    info: "Be sure all fields are completed before saving.",
    target: "save-button",
    modalOffset: { x: -40, y: 0 }
  }
]

export const rentedPropertiesPaymentSteps = [
  {
    title: "Tenant Payment Interface",
    content: `
      <p class="mb-3">This is what tenants see when making <strong>online rent payments</strong>.</p>
      <p>The interface is designed to be simple and secure for tenant use.</p>
    `,
    info: "A user-friendly payment interface encourages on-time payments.",
    target: null
  },
  {
    title: "Payment Amount Display",
    content: `
      <p class="mb-3">Tenants see their <strong>payment amount</strong> and due date clearly displayed.</p>
      <p>This includes base rent plus any additional fees or charges.</p>
    `,
    info: "Clear payment amounts reduce confusion and support questions.",
    target: "payment-amount"
  },
  {
    title: "Payment Methods",
    content: `
      <p class="mb-3">Tenants can choose from available <strong>payment methods</strong>:</p>
      <ul class="list-disc ml-5 space-y-1">
        <li>Bank account (ACH) transfer</li>
        <li>Debit card payments</li>
        <li>Credit card payments (if enabled)</li>
      </ul>
    `,
    info: "Multiple payment options increase the likelihood of on-time payments.",
    target: "payment-methods"
  },
  {
    title: "Payment Confirmation",
    content: `
      <p class="mb-3">After payment, tenants receive <strong>confirmation</strong> and receipt.</p>
      <p>Both tenant and landlord receive email confirmations of successful payments.</p>
    `,
    info: "Payment confirmations provide important records for both parties.",
    target: "payment-confirmation"
  }
]

export const rentedPropertiesPaymentSucceededSteps = [
  {
    title: "Payment Success",
    content: `
      <p class="mb-3">This confirmation shows a <strong>successful payment</strong> has been processed.</p>
      <p>Both tenant and landlord receive notifications of the completed transaction.</p>
    `,
    info: "Payment confirmations provide proof of payment for both parties.",
    target: null
  },
  {
    title: "Payment Details",
    content: `
      <p class="mb-3">Review the <strong>payment details</strong>:</p>
      <ul class="list-disc ml-5 space-y-1">
        <li>Payment amount and date</li>
        <li>Property address</li>
        <li>Payment method used</li>
        <li>Transaction reference number</li>
      </ul>
    `,
    info: "Keep payment confirmations for your financial records.",
    target: "payment-details"
  },
  {
    title: "Next Steps",
    content: `
      <p class="mb-3">Information about <strong>next steps</strong>:</p>
      <ul class="list-disc ml-5 space-y-1">
        <li>When funds will be deposited</li>
        <li>Receipt delivery information</li>
        <li>Contact info for questions</li>
      </ul>
    `,
    info: "Payments typically take 2-3 business days to appear in your bank account.",
    target: "next-steps"
  }
]

export const rentedPropertiesPaymentFailedSteps = [
  {
    title: "Payment Failed",
    content: `
      <p class="mb-3">This page shows information when a <strong>payment attempt fails</strong>.</p>
      <p>Common reasons include insufficient funds or expired payment methods.</p>
    `,
    info: "Failed payments require immediate attention to avoid late fees.",
    target: null
  },
  {
    title: "Failure Reason",
    content: `
      <p class="mb-3">Review the <strong>reason for payment failure</strong>:</p>
      <ul class="list-disc ml-5 space-y-1">
        <li>Insufficient account funds</li>
        <li>Expired or invalid payment method</li>
        <li>Bank processing issues</li>
        <li>Account verification problems</li>
      </ul>
    `,
    info: "Understanding the failure reason helps prevent future payment issues.",
    target: "failure-reason"
  },
  {
    title: "Retry Payment Options",
    content: `
      <p class="mb-3">Options to <strong>retry the payment</strong>:</p>
      <ul class="list-disc ml-5 space-y-1">
        <li>Try the same payment method again</li>
        <li>Use a different payment method</li>
        <li>Update payment method information</li>
        <li>Contact support for assistance</li>
      </ul>
    `,
    info: "Quick retry helps avoid late fees and maintains good payment history.",
    target: "retry-options"
  }
]

export const rentedPropertiesSubscriptionIndexSteps = [
  {
    title: "Rent Collection Subscription",
    content: `
      <p class="mb-3">Manage your <strong>rent collection subscription</strong> and billing.</p>
      <p>Different plans offer varying features and property limits.</p>
    `,
    info: "Choose a plan that fits your property portfolio size and needs.",
    target: null
  },
  {
    title: "Current Plan Details",
    content: `
      <p class="mb-3">View your <strong>current subscription plan</strong>:</p>
      <ul class="list-disc ml-5 space-y-1">
        <li>Plan name and features</li>
        <li>Number of properties included</li>
        <li>Monthly/annual pricing</li>
        <li>Next billing date</li>
      </ul>
    `,
    info: "Your plan determines how many properties can use online rent collection.",
    target: "current-plan"
  },
  {
    title: "Billing Management",
    content: `
      <p class="mb-3">Manage your <strong>subscription billing</strong>:</p>
      <ul class="list-disc ml-5 space-y-1">
        <li>Update payment methods</li>
        <li>View billing history</li>
        <li>Change billing frequency</li>
        <li>Download invoices</li>
      </ul>
    `,
    info: "Keep billing information current to avoid service interruptions.",
    target: "billing-management"
  }
]